/**
 * 구역 나누기의 모양과 구역들(2026-11-03) — 모양을 고르면 구역 수가 따라 바뀌고, 구역마다 그림·영상·HTML 여러 장(정한 간격마다
 * 넘긴다) 또는 글을 고른다. 장 하나하나는 반반 화면의 그림 칸과 같은 칸(`MediaField`)이다.
 *
 * 모양을 줄였다 늘리거나 구역을 글로 바꿨다 되돌려도 이 창을 닫기 전에는 하던 것이 남는다(`stash`) — 저장하는 내용에는 지금
 * 모양의 구역만 들어간다(서버가 구역 수를 맞춰 본다).
 */
import { useRef, type ReactNode } from 'react';
import { ZONE_BOXES, ZONE_COUNTS, ZONE_LAYOUTS, ZONE_MAX_ITEMS, type ZoneLayout } from '@signageone/core';
import { HelpTip } from '../HelpTip.js';
import { NumberInput, numberProblem } from './NumberInput.js';
import { MediaField, readMediaValue, type MediaFieldHost, type MediaValue } from './MediaField.js';

type MediaZone = { kind: 'media'; items: MediaValue[]; dwellSec: number | null };
type TextZone = { kind: 'text'; badge: string; title: string; body: string };
type ZoneValue = MediaZone | TextZone;

export const LAYOUT_LABEL: Readonly<Record<ZoneLayout, string>> = {
  halves: '둘로 똑같이',
  'main-side': '크게 하나 + 옆 하나',
  'main-pair': '크게 하나 + 옆 둘',
  thirds: '셋으로 똑같이',
  quad: '넷으로 (2×2)',
};

/** 구역의 이름 — 가로 화면의 자리로 부르고, 세로 화면에서 달라지면 함께 적는다. */
const ZONE_NAMES: Readonly<Record<ZoneLayout, readonly string[]>> = {
  halves: ['왼쪽 (세로 화면은 위)', '오른쪽 (세로 화면은 아래)'],
  'main-side': ['큰 구역 — 왼쪽 (세로 화면은 위)', '옆 구역 — 오른쪽 (세로 화면은 아래)'],
  'main-pair': [
    '큰 구역 — 왼쪽 (세로 화면은 위)',
    '옆 위 (세로 화면은 아래 왼쪽)',
    '옆 아래 (세로 화면은 아래 오른쪽)',
  ],
  thirds: ['왼쪽 (세로 화면은 위)', '가운데', '오른쪽 (세로 화면은 아래)'],
  quad: ['왼쪽 위', '오른쪽 위', '왼쪽 아래', '오른쪽 아래'],
};

const DWELL_RULE = { label: '넘기는 간격', min: 3, max: 600, unit: '초' };

export const blankZone = (kind: ZoneValue['kind']): ZoneValue =>
  kind === 'media' ? { kind, items: [], dwellSec: 10 } : { kind, badge: '', title: '제목을 입력하세요', body: '' };

const isLayout = (value: unknown): value is ZoneLayout =>
  typeof value === 'string' && (ZONE_LAYOUTS as readonly string[]).includes(value);

/** 저장된 구역을 폼의 모양으로 — 모르는 것은 빈 그림 구역. */
export function readZone(raw: unknown): ZoneValue {
  const value = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  if (value.kind === 'text') {
    const text = (key: string): string => (typeof value[key] === 'string' ? (value[key] as string) : '');
    return { kind: 'text', badge: text('badge'), title: text('title'), body: text('body') };
  }
  const items = Array.isArray(value.items) ? value.items.flatMap((item) => readMediaValue(item) ?? []) : [];
  // 비운 간격(null)은 그대로 — 지우고 다시 치는 중이다. 적지 않았으면 기본 10초.
  return {
    kind: 'media',
    items,
    dwellSec: typeof value.dwellSec === 'number' ? value.dwellSec : value.dwellSec === null ? null : 10,
  };
}

/** 모양의 구역 수에 맞춘 구역들 — 남는 것은 덜고, 모자라면 먼저 해 둔 것(`stash`)이나 빈 그림 구역으로 채운다. */
export function zonesForLayout(
  zones: readonly ZoneValue[],
  count: number,
  stash: readonly ZoneValue[] = [],
): ZoneValue[] {
  return Array.from({ length: count }, (_, index) => zones[index] ?? stash[index] ?? blankZone('media'));
}

export function ZonesField({
  label,
  help,
  value,
  errors,
  host,
  disabled,
  onChange,
}: {
  label: string;
  help?: string;
  /** 템플릿 데이터 전체(`layout`·`zones`·`ticker`). */
  value: unknown;
  errors: ReadonlyMap<string, string>;
  host: MediaFieldHost | undefined;
  disabled: boolean;
  onChange: (next: unknown) => void;
}): ReactNode {
  const data = (value && typeof value === 'object' ? value : {}) as Record<string, unknown>;
  const layout: ZoneLayout = isLayout(data.layout) ? data.layout : 'main-side';
  const zones = zonesForLayout((Array.isArray(data.zones) ? data.zones : []).map(readZone), ZONE_COUNTS[layout]);
  // 이 창에서 해 둔 구역 — 모양을 줄였다 늘리거나 글로 바꿨다 되돌릴 때 다시 쓴다.
  const stash = useRef<{ zones: ZoneValue[]; kinds: Map<string, ZoneValue> }>({ zones: [], kinds: new Map() });

  const write = (nextLayout: ZoneLayout, nextZones: ZoneValue[]): void => {
    nextZones.forEach((zone, index) => {
      stash.current.zones[index] = zone;
      stash.current.kinds.set(`${index}:${zone.kind}`, zone);
    });
    onChange({ ...data, layout: nextLayout, zones: nextZones });
  };
  const setZone = (index: number, zone: ZoneValue): void =>
    write(
      layout,
      zones.map((current, at) => (at === index ? zone : current)),
    );

  return (
    <div className="field-stack">
      <div className="field">
        <span className="field-label">
          {label}
          {help ? <HelpTip text={help} label={`${label} 도움말`} /> : null}
        </span>
        <div className="zone-layouts" role="radiogroup" aria-label={label}>
          {ZONE_LAYOUTS.map((choice) => (
            <button
              key={choice}
              type="button"
              role="radio"
              aria-checked={choice === layout}
              className={`zone-layout${choice === layout ? ' is-on' : ''}`}
              disabled={disabled}
              onClick={() =>
                choice !== layout && write(choice, zonesForLayout(zones, ZONE_COUNTS[choice], stash.current.zones))
              }
            >
              <LayoutGlyph layout={choice} />
              <span>{LAYOUT_LABEL[choice]}</span>
            </button>
          ))}
        </div>
        {errors.get('zones') ? <p className="field-error">{errors.get('zones')}</p> : null}
      </div>

      {zones.map((zone, index) => (
        <ZoneEditor
          key={index}
          index={index}
          layout={layout}
          zone={zone}
          errors={errors}
          host={host}
          disabled={disabled}
          onKind={(kind) => setZone(index, stash.current.kinds.get(`${index}:${kind}`) ?? blankZone(kind))}
          onChange={(next) => setZone(index, next)}
        />
      ))}
    </div>
  );
}

/** 모양 그림 — 가로 화면의 구역 자리를 줄여 그린다. */
function LayoutGlyph({ layout, highlight }: { layout: ZoneLayout; highlight?: number }): ReactNode {
  return (
    <span className="zone-glyph" aria-hidden="true">
      {ZONE_BOXES[layout].landscape.map((box, index) => (
        <span
          key={index}
          className={`zone-glyph__box${highlight === index ? ' is-on' : ''}`}
          style={{
            left: `${(box.left / 1840) * 100}%`,
            top: `${(box.top / 852) * 100}%`,
            width: `${(box.width / 1840) * 100}%`,
            height: `${(box.height / 852) * 100}%`,
          }}
        >
          {index + 1}
        </span>
      ))}
    </span>
  );
}

function ZoneEditor({
  index,
  layout,
  zone,
  errors,
  host,
  disabled,
  onKind,
  onChange,
}: {
  index: number;
  layout: ZoneLayout;
  zone: ZoneValue;
  errors: ReadonlyMap<string, string>;
  host: MediaFieldHost | undefined;
  disabled: boolean;
  onKind: (kind: ZoneValue['kind']) => void;
  onChange: (next: ZoneValue) => void;
}): ReactNode {
  const path = `zones.${index}`;
  const wide = ZONE_BOXES[layout].landscape[index];
  const tall = ZONE_BOXES[layout].portrait[index];
  return (
    <section className="field-item zone-editor" aria-label={`구역 ${index + 1}`}>
      <div className="field-item-head">
        <span className="zone-editor__title">
          <LayoutGlyph layout={layout} highlight={index} />
          <span>
            <span className="field-item-num">
              구역 {index + 1} · {ZONE_NAMES[layout][index]}
            </span>
            {wide && tall ? (
              <span className="hint block">
                가로 화면 {wide.width}×{wide.height} · 세로 화면 {tall.width}×{tall.height}
              </span>
            ) : null}
          </span>
        </span>
        <div className="seg seg-sm" role="group" aria-label={`구역 ${index + 1} 내용`}>
          {(['media', 'text'] as const).map((kind) => (
            <button
              key={kind}
              type="button"
              className="seg-btn"
              aria-pressed={zone.kind === kind}
              disabled={disabled}
              onClick={() => zone.kind !== kind && onKind(kind)}
            >
              {kind === 'media' ? '그림·영상·HTML' : '글'}
            </button>
          ))}
        </div>
      </div>
      {zone.kind === 'media' ? (
        <MediaZoneEditor path={path} zone={zone} errors={errors} host={host} disabled={disabled} onChange={onChange} />
      ) : (
        <TextZoneEditor path={path} zone={zone} errors={errors} disabled={disabled} onChange={onChange} />
      )}
    </section>
  );
}

function MediaZoneEditor({
  path,
  zone,
  errors,
  host,
  disabled,
  onChange,
}: {
  path: string;
  zone: MediaZone;
  errors: ReadonlyMap<string, string>;
  host: MediaFieldHost | undefined;
  disabled: boolean;
  onChange: (next: ZoneValue) => void;
}): ReactNode {
  const setItems = (items: MediaValue[]): void => onChange({ ...zone, items });
  const move = (from: number, to: number): void => {
    if (to < 0 || to >= zone.items.length) return;
    const items = [...zone.items];
    const [moved] = items.splice(from, 1);
    items.splice(to, 0, moved as MediaValue);
    setItems(items);
  };
  const dwellError = errors.get(`${path}.dwellSec`);
  return (
    <div className="field-stack">
      {zone.items.map((item, at) => (
        <div key={`${at}-${item.assetId}`} className="zone-slide">
          {zone.items.length > 1 ? (
            <div className="field-item-actions zone-slide__actions">
              <button
                type="button"
                className="link"
                aria-label="앞으로"
                disabled={disabled || at === 0}
                onClick={() => move(at, at - 1)}
              >
                ↑
              </button>
              <button
                type="button"
                className="link"
                aria-label="뒤로"
                disabled={disabled || at === zone.items.length - 1}
                onClick={() => move(at, at + 1)}
              >
                ↓
              </button>
            </div>
          ) : null}
          <MediaField
            label={zone.items.length > 1 ? `${at + 1}번째 장` : '그림·영상·HTML'}
            error={
              errors.get(`${path}.items.${at}.assetId`) ??
              errors.get(`${path}.items.${at}.area`) ??
              errors.get(`${path}.items.${at}`)
            }
            value={item}
            host={host}
            disabled={disabled}
            onChange={(next) =>
              setItems(
                next
                  ? zone.items.map((current, i) => (i === at ? next : current))
                  : zone.items.filter((_, i) => i !== at),
              )
            }
          />
        </div>
      ))}
      {zone.items.length < ZONE_MAX_ITEMS ? (
        <MediaField
          key={`add-${zone.items.length}`}
          label={zone.items.length === 0 ? '그림·영상·HTML' : '장 더하기'}
          value={null}
          host={host}
          disabled={disabled}
          emptyHint={
            zone.items.length === 0
              ? '고르지 않았습니다 — 그 자리에 장식 무늬가 나옵니다.'
              : '더 고르면 정한 간격마다 다음 장으로 넘깁니다.'
          }
          onChange={(next) => next && setItems([...zone.items, next])}
        />
      ) : null}
      {errors.get(`${path}.items`) ? <p className="field-error">{errors.get(`${path}.items`)}</p> : null}
      {zone.items.length > 1 ? (
        <label className="field">
          <span className="field-label">넘기는 간격 (초)</span>
          <NumberInput
            rule={DWELL_RULE}
            value={zone.dwellSec}
            invalid={Boolean(dwellError)}
            disabled={disabled}
            onChange={(next) => onChange({ ...zone, dwellSec: next })}
          />
          {dwellError ? <p className="field-error">{numberProblem(zone.dwellSec, DWELL_RULE) ?? dwellError}</p> : null}
        </label>
      ) : null}
    </div>
  );
}

function TextZoneEditor({
  path,
  zone,
  errors,
  disabled,
  onChange,
}: {
  path: string;
  zone: TextZone;
  errors: ReadonlyMap<string, string>;
  disabled: boolean;
  onChange: (next: ZoneValue) => void;
}): ReactNode {
  const input = (key: 'badge' | 'title', label: string, max: number, hint?: string): ReactNode => (
    <label className="field">
      <span className="field-label">
        {label}
        {hint ? <HelpTip text={hint} label={`${label} 도움말`} /> : null}
        <em className="field-count">
          {Array.from(zone[key]).length} / {max}
        </em>
      </span>
      <input
        className="input"
        value={zone[key]}
        maxLength={max}
        disabled={disabled}
        onChange={(event) => onChange({ ...zone, [key]: event.target.value })}
      />
      {errors.get(`${path}.${key}`) ? <p className="field-error">{errors.get(`${path}.${key}`)}</p> : null}
    </label>
  );
  return (
    <div className="field-stack">
      {input('badge', '배지', 20, '비우면 표시되지 않습니다.')}
      {input('title', '제목', 60)}
      <label className="field">
        <span className="field-label">
          설명
          <em className="field-count">{Array.from(zone.body).length} / 300</em>
        </span>
        <textarea
          className="input input-textarea"
          rows={3}
          value={zone.body}
          maxLength={300}
          disabled={disabled}
          onChange={(event) => onChange({ ...zone, body: event.target.value })}
        />
        {errors.get(`${path}.body`) ? <p className="field-error">{errors.get(`${path}.body`)}</p> : null}
      </label>
    </div>
  );
}
